import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, Lock, XCircle } from 'lucide-react';
import { supabase, getErrorMessage } from '../services/supabaseClient';

const getParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const [k, v] of searchParams.entries()) out[k] = v;

  // HashRouter uses '#/route', Supabase may append a second fragment: '#access_token=...'
  const hash = window.location.hash || '';
  const hashParts = hash.split('#');
  // e.g. ["", "/reset-password", "access_token=...&refresh_token=...&type=recovery"]
  if (hashParts.length >= 3) {
    const frag = hashParts.slice(2).join('#');
    const fragParams = new URLSearchParams(frag);
    for (const [k, v] of fragParams.entries()) out[k] = v;
  } else if (hash.includes('access_token=')) {
    // Best effort: parse anything after the first '#'
    const frag = hash.substring(1);
    const fragParams = new URLSearchParams(frag);
    for (const [k, v] of fragParams.entries()) out[k] = v;
  }

  return out;
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => (typeof window !== 'undefined' ? getParams() : {}), []);

  useEffect(() => {
    let active = true;

    const init = async () => {
      try {
        // 1) PKCE flow: ?code=...
        if (params.code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(params.code);
          if (exErr) throw exErr;
        }

        // 2) Implicit flow: #access_token=...&refresh_token=...
        if (params.access_token && params.refresh_token) {
          const { error: setErr } = await supabase.auth.setSession({
            access_token: params.access_token,
            refresh_token: params.refresh_token
          });
          if (setErr) throw setErr;
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('Reset link is invalid or expired. Please request a new one.');
        }

        if (!active) return;
        setReady(true);
      } catch (e) {
        if (!active) return;
        setError(getErrorMessage(e));
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    void init();
    return () => { active = false; };
  }, [params]);

  const validate = () => {
    if (password.length < 8) return 'Password must be at least 8 characters.';
    if (password !== confirm) return 'Passwords do not match.';
    return null;
  };

  const handleSave = async () => {
    setError(null);
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSaving(true);
    try {
      const { error: upErr } = await supabase.auth.updateUser({ password });
      if (upErr) throw upErr;
      setSuccess(true);
      // End recovery session and send them to login
      await supabase.auth.signOut();
      setTimeout(() => navigate('/login'), 1500);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
        <h2 className="text-xl font-bold text-slate-800 dark:text-white">Preparing reset…</h2>
        <p className="text-slate-500 dark:text-slate-400">Validating your link.</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
        <XCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Reset link error</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-md">{error}</p>
        <button
          onClick={() => navigate('/login')}
          className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
        >
          Back to login
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="p-8 space-y-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Set a new password</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Choose a strong password you don’t use elsewhere.</p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-800 dark:text-green-200 flex items-center gap-2">
              <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" />
              Password updated. Redirecting to login…
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">New password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Confirm password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving || success}
            className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              'Update password'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

